import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../Utility/Languagecontext";
import { HERO_IMGS, HERO_VIDEOS } from "../Utility/Media";

const EASE = [0.76, 0, 0.24, 1];
const PANELS = 3; // عدد الفيديوهات المعروضة جنب بعض

// ترتيب دخول الألواح: الأوسط الأول وبعده الجنبين
const DELAYS = [0.3, 0, 0.3];

// الـ App بيحط data-intro="wait" على <html> طول ما الـ Preloader ظاهر، ويقلبها "go" لما يبدأ يتفتح
const introReady = () => document.documentElement.getAttribute("data-intro") !== "wait";

const useIntroReady = () => {
  const [ready, setReady] = useState(introReady);
  useEffect(() => {
    const mo = new MutationObserver(() => setReady(introReady()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-intro"] });
    setReady(introReady());
    return () => mo.disconnect();
  }, []);
  return ready;
};

// اتصال ضعيف أو "توفير البيانات" مفعّل: نعرض صور بدل 3 فيديوهات (أسرع بكتير على الموبايل)
const liteMode = () => {
  if (typeof navigator === "undefined") return false;
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || "")));
};

const useMedia = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return matches;
};

export const HeroSection = () => {
  const { t, lang } = useLang();
  const ready = useIntroReady();
  const stacked = useMedia("(max-width: 760px)"); // موبايل: الألواح فوق بعض
  const ref = useRef(null);
  const stageRef = useRef(null);
  const contentRef = useRef(null);
  const panelEls = useRef([]);
  const vids = useRef([]);
  const lastTimes = useRef([]); // آخر وقت تشغيل لكل فيديو، عشان نكتشف التجمّد
  const reduceMotion = useReducedMotion();
  const [lite] = useState(liteMode);
  const inView = useInView(ref, { amount: 0 });

  // نسخة من inView ليها ref، عشان الـ interval يقرا القيمة الحالية من غير ما يتعمله إعادة تشغيل
  const inViewRef = useRef(false);
  inViewRef.current = inView;

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  // مع السكرول: الفيديوهات بتتحرك بهدوء وتكبر شوية، والمحتوى بيطلع ويختفي
  const mediaY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mediaScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 0.8], ["0vh", "-12vh"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const videos = lite ? [] : (HERO_VIDEOS || []).filter(Boolean);
  const posters = (HERO_IMGS || []).filter(Boolean);

  // لو الفيديوهات أقل من 3، اللوح الناقص بيعيد فيديو من نقطة تانية
  const panels = Array.from({ length: PANELS }, (_, i) => ({
    src: videos.length ? videos[i % videos.length] : null,
    poster: posters.length ? posters[i % posters.length] : undefined,
    start: videos.length && i >= videos.length ? 0.3 + 0.25 * (i - videos.length) : 0,
  }));

  // الفيديوهات بتشتغل بس لما الهيرو ظاهر (توفير للأداء)
  useEffect(() => {
    vids.current.forEach((v) => {
      if (!v) return;
      if (inView && !reduceMotion) {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [inView, reduceMotion]);

  // شبكة أمان: كل ثانية ونص بنفحص الفيديوهات
  // - لو فيديو واقف (paused) وهو ظاهر: نشغّله
  // - لو فيديو شغال بس متجمّد (الوقت مش بيتحرك): نحرّكه شوية ونشغّله تاني
  useEffect(() => {
    if (reduceMotion) return;
    const id = setInterval(() => {
      if (!inViewRef.current || document.hidden) return;
      vids.current.forEach((v, i) => {
        if (!v || v.ended) return;

        if (v.paused) {
          v.play().catch(() => {});
          lastTimes.current[i] = v.currentTime;
          return;
        }

        const frozen = v.readyState >= 2 && v.currentTime === lastTimes.current[i];
        if (frozen) {
          try {
            v.currentTime = v.currentTime + 0.05;
          } catch (e) {
            /* مش مشكلة */
          }
          v.play().catch(() => {});
        }
        lastTimes.current[i] = v.currentTime;
      });
    }, 1500);
    return () => clearInterval(id);
  }, [reduceMotion]);

  // موبايل (الفيديوهات فوق بعض): المحتوى بيخلص عند آخر الفيديو التاني ومبينزلش على التالت.
  // بنقيس مكان آخر الفيديو التاني، ولو المحتوى أطول من المساحة بنصغّر العنوان شوية لحد ما يدخل.
  useEffect(() => {
    const stage = stageRef.current;
    const content = contentRef.current;
    if (!stage || !content) return undefined;
    if (!stacked) {
      stage.style.removeProperty("--hero-cut");
      content.style.removeProperty("--hfit");
      return undefined;
    }
    let raf = 0;
    const fit = () => {
      raf = 0;
      const p2 = panelEls.current[1];
      if (!p2) return;
      const end = p2.offsetTop + p2.offsetHeight;
      stage.style.setProperty("--hero-cut", `${Math.max(0, stage.clientHeight - end)}px`);
      // الطول الحقيقي للمحتوى (من غير حركات الدخول) مقارنة بالمساحة المتاحة
      const cs = getComputedStyle(content);
      const room = content.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      const flow = [...content.children].filter((el) => {
        const p = getComputedStyle(el).position;
        return p !== "absolute" && p !== "fixed" && el.offsetHeight > 0;
      });
      if (!flow.length) return;
      const need = () => {
        const first = flow[0];
        const last = flow[flow.length - 1];
        return last.offsetTop + last.offsetHeight - first.offsetTop;
      };
      let f = 1;
      content.style.setProperty("--hfit", "1");
      while (need() > room + 1 && f > 0.6) {
        f -= 0.04;
        content.style.setProperty("--hfit", f.toFixed(2));
      }
    };
    const queue = () => {
      if (!raf) raf = requestAnimationFrame(fit);
    };
    fit();
    const ro = new ResizeObserver(queue);
    ro.observe(stage);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(queue);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [stacked, lang]);

  // أنيميشن الدخول بيستنى الـ Preloader
  const on = (target) => (ready ? target : undefined);

  // الدخول: اللوح الأول والتالت يطلعوا من تحت، والأوسط ينزل من فوق (زي حركة الـ Preloader)
  // وعلى الموبايل بيدخلوا من الجنب بالتبادل
  const hiddenClip = (i) =>
    stacked
      ? i % 2 === 0
        ? "inset(0% 100% 0% 0%)"
        : "inset(0% 0% 0% 100%)"
      : i % 2 === 0
      ? "inset(100% 0% 0% 0%)"
      : "inset(0% 0% 100% 0%)";

  const hiddenShift = (i) =>
    stacked ? { x: i % 2 === 0 ? "-12%" : "12%" } : { y: i % 2 === 0 ? "14%" : "-14%" };

  return (
    <section className="hero" id="home" ref={ref}>
      <div className="hero__stage" ref={stageRef}>
        <div className="hero__media">
          <motion.div className="hero__parallax" style={{ y: mediaY, scale: mediaScale }}>
            <div className="hero__panels">
              {panels.map((p, i) => (
                <motion.div
                  key={i}
                  ref={(el) => (panelEls.current[i] = el)}
                  className="hpanel"
                  initial={reduceMotion ? false : { clipPath: hiddenClip(i) }}
                  animate={reduceMotion ? undefined : on({ clipPath: "inset(0% 0% 0% 0%)" })}
                  transition={{ duration: 1.6, delay: DELAYS[i], ease: EASE }}
                >
                  {/* الفيديو جوه بيتحرك عكس اللوح وبيصغّر، فالدخول يبان فيه عمق */}
                  <motion.div
                    className="hpanel__inner"
                    initial={reduceMotion ? false : { scale: 1.35, ...hiddenShift(i) }}
                    animate={reduceMotion ? undefined : on({ scale: 1, x: 0, y: 0 })}
                    transition={{ duration: 2.2, delay: DELAYS[i], ease: EASE }}
                  >
                    {p.src ? (
                      <video
                        ref={(el) => (vids.current[i] = el)}
                        className="hpanel__video"
                        src={p.src}
                        poster={p.poster}
                        muted
                        loop
                        playsInline
                        autoPlay={!reduceMotion}
                        preload={i === 1 ? "auto" : "metadata"}
                        disablePictureInPicture
                        tabIndex={-1}
                        onLoadedMetadata={(e) => {
                          const v = e.currentTarget;
                          if (p.start && v.duration) v.currentTime = v.duration * p.start;
                        }}
                        // لو المتصفح وقف الفيديو أو استنى بيانات، نحاول نكمّله
                        onStalled={(e) => e.currentTarget.play().catch(() => {})}
                        onSuspend={(e) => {
                          const v = e.currentTarget;
                          if (inViewRef.current && v.paused) v.play().catch(() => {});
                        }}
                      />
                    ) : (
                      p.poster && <img className="hpanel__video" src={p.poster} alt="" />
                    )}
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <div className="hero__shade" />
        </div>

        {/* آخر الفيديو بيدوب في لون الخلفية عشان يندمج مع الأبوت */}
        <div className="hero__fade" />

        <motion.div className="hero__content" ref={contentRef} style={{ y: contentY, opacity: contentOpacity }}>
          <motion.p
            className="hero__eyebrow"
            key={`eb-${lang}`}
            initial={{ opacity: 0, x: -20 }}
            animate={on({ opacity: 1, x: 0 })}
            transition={{ delay: 1.1, duration: 1 }}
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="hero__title">
            {t.hero.lines.map((line, i) => (
              <span className="hero__line" key={`${lang}-${i}`}>
                <motion.span
                  initial={{ y: "120%" }}
                  animate={on({ y: 0 })}
                  transition={{ duration: 1.2, delay: 1.0 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            className="hero__foot"
            key={`ft-${lang}`}
            initial={{ opacity: 0, y: 30 }}
            animate={on({ opacity: 1, y: 0 })}
            transition={{ delay: 1.7, duration: 1 }}
          >
            <p className="hero__sub">{t.hero.sub}</p>
            <div className="hero__btns">
              <a href="#projects" className="btn btn--solid">
                {t.hero.primary}
              </a>
              <a href="#contact" className="btn btn--ghost">
                {t.hero.secondary}
              </a>
            </div>
          </motion.div>

          <motion.aside
            className="hero__card"
            key={`cd-${lang}`}
            initial={{ opacity: 0, y: 40 }}
            animate={on({ opacity: 1, y: 0 })}
            transition={{ delay: 2, duration: 1 }}
          >
            <span className="hero__card-label">{t.hero.card.title}</span>
            <ul className="hero__facts">
              {t.hero.card.items.map((it) => (
                <li key={it.v}>
                  <strong>{it.k}</strong>
                  <span>{it.v}</span>
                </li>
              ))}
            </ul>
          </motion.aside>

          <div className="hero__scroll" aria-hidden="true">
            <span>{t.hero.scroll}</span>
            <i />
          </div>
        </motion.div>
      </div>
    </section>
  );
};